import { createContext, createElement, useContext, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_LANGUAGE_CODE,
  getLanguageByCode,
  normalizeUiLanguageCode,
  resolveInitialLocale,
} from "./languages.js";
import {
  translateUiCopy,
} from "./i18n-copy.js";

const STORAGE_KEY = "mincool.uiLanguageCode";

export const MESSAGES = {
  "en": {
    "common.language": "Language",
    "common.continue": "Continue",
    "common.back": "Back",
    "common.skip_for_now": "Skip for now",
    "landing.sign_in_up": "Sign in / up",
    "landing.hero_line_1": "Say goodbye to long",
    "landing.hero_line_2": "voice notes.",
    "landing.subtitle": "Min transcribes and summarises your voice notes.\nA 7 minute voice note turns into a 7 second read.",
    "landing.cta": "Get 1 Month Free",
    "landing.free_forever": "1 month of Fun free",
    "landing.locale_prompt": "See this page in {language}?",
    "landing.use_language": "Use {language}",
    "landing.keep_english": "Keep English",
    "auth.language_eyebrow": "Set your language",
    "auth.language_title": "What language should Min use with you?",
    "auth.language_copy": "Pick your home language first, then any other languages Min can use when people send you voice notes.",
    "auth.home_language": "Home language",
    "auth.other_languages": "Other languages Min can use with you",
    "auth.other_languages_help": "Choose languages you understand well enough to receive summaries in.",
    "auth.continue_with_language": "Continue",
    "auth.linked_eyebrow": "Linked successfully",
    "auth.linked_title": "Add your email",
    "changelog.preview_eyebrow": "Latest update",
    "changelog.preview_cta": "See what is new",
    "changelog.home": "Home",
    "changelog.details": "Details",
    "changelog.also_improved": "Also improved",
    "changelog.image_alt": "Editorial Min changelog artwork for {title}",
    "changelog.pagination": "Changelog pagination",
    "changelog.read_entry": "Read update",
    "changelog.next_entry": "Next",
    "changelog.not_found": "Update not found",
    "changelog.back_to_changelog": "Back to changelog",
    "changelog.eyebrow": "Changelog",
    "changelog.title": "What Min shipped",
    "changelog.subtitle": "Short updates on the product work that makes WhatsApp easier, faster, and more useful."
  },
  "af": {
    "common.language": "Taal",
    "common.continue": "Gaan voort",
    "common.back": "Terug",
    "common.skip_for_now": "Slaan nou oor",
    "landing.sign_in_up": "Teken in / registreer",
    "landing.hero_line_1": "Sê totsiens vir",
    "landing.hero_line_2": "lang stemnotas.",
    "landing.subtitle": "Min transkribeer en som jou stemnotas op.\n'n Stemnota van 7 minute word 'n leesstuk van 7 sekondes.",
    "landing.cta": "Kry 1 maand gratis",
    "landing.free_forever": "1 maand Fun gratis",
    "landing.locale_prompt": "Wys hierdie blad in {language}?",
    "landing.use_language": "Gebruik {language}",
    "landing.keep_english": "Hou Engels",
    "auth.language_eyebrow": "Kies jou taal",
    "auth.language_title": "Watter taal moet Min met jou gebruik?",
    "auth.language_copy": "Kies eers jou huistaal, dan enige ander tale wat Min met jou kan gebruik wanneer mense stemnotas stuur.",
    "auth.home_language": "Huistaal",
    "auth.other_languages": "Ander tale wat Min met jou kan gebruik",
    "auth.other_languages_help": "Kies tale wat jy goed genoeg verstaan om opsommings in te ontvang.",
    "auth.continue_with_language": "Gaan voort",
    "auth.linked_eyebrow": "Suksesvol gekoppel",
    "auth.linked_title": "Voeg jou e-pos by",
    "changelog.preview_eyebrow": "Nuutste opdatering",
    "changelog.preview_cta": "Sien wat nuut is",
    "changelog.home": "Tuis",
    "changelog.details": "Besonderhede",
    "changelog.also_improved": "Ook verbeter",
    "changelog.image_alt": "Redaksionele Min-veranderingslog-kunswerk vir {title}",
    "changelog.pagination": "Veranderingslog-navigasie",
    "changelog.read_entry": "Lees opdatering",
    "changelog.next_entry": "Volgende",
    "changelog.not_found": "Opdatering nie gevind nie",
    "changelog.back_to_changelog": "Terug na veranderingslog",
    "changelog.eyebrow": "Veranderingslog",
    "changelog.title": "Wat Min gestuur het",
    "changelog.subtitle": "Kort opdaterings oor die produkwerk wat WhatsApp makliker, vinniger en nuttiger maak."
  },
  "hi": {
    "common.language": "भाषा",
    "common.continue": "जारी रखें",
    "common.back": "वापस",
    "common.skip_for_now": "अभी छोड़ें",
    "landing.sign_in_up": "साइन इन / अप",
    "landing.hero_line_1": "लंबे वॉइस नोट्स को",
    "landing.hero_line_2": "अलविदा कहें।",
    "landing.subtitle": "Min आपके वॉइस नोट्स को लिखित रूप में बदलता और उनका सारांश बनाता है।\n7 मिनट का वॉइस नोट 7 सेकंड में पढ़ा जा सकता है।",
    "landing.cta": "1 महीना मुफ़्त पाएं",
    "landing.free_forever": "Fun का 1 महीना मुफ्त",
    "landing.locale_prompt": "क्या यह पेज {language} में देखें?",
    "landing.use_language": "{language} का उपयोग करें",
    "landing.keep_english": "English रखें",
    "auth.language_eyebrow": "अपनी भाषा चुनें",
    "auth.language_title": "Min आपसे किस भाषा में बात करे?",
    "auth.language_copy": "पहले अपनी मुख्य भाषा चुनें, फिर वे दूसरी भाषाएं जिन्हें Min वॉइस नोट सारांश के लिए इस्तेमाल कर सकता है.",
    "auth.home_language": "मुख्य भाषा",
    "auth.other_languages": "दूसरी भाषाएं जिन्हें Min आपके साथ इस्तेमाल कर सकता है",
    "auth.other_languages_help": "वे भाषाएं चुनें जिन्हें आप इतना समझते हैं कि उनमें सारांश मिल सके.",
    "auth.continue_with_language": "जारी रखें",
    "auth.linked_eyebrow": "सफलतापूर्वक लिंक हो गया",
    "auth.linked_title": "अपना ईमेल जोड़ें",
    "changelog.preview_eyebrow": "नया अपडेट",
    "changelog.preview_cta": "नया क्या है देखें",
    "changelog.home": "होम",
    "changelog.details": "विवरण",
    "changelog.also_improved": "यह भी बेहतर हुआ",
    "changelog.image_alt": "{title} के लिए Min चेंजलॉग की संपादकीय कलाकृति",
    "changelog.pagination": "चेंजलॉग नेविगेशन",
    "changelog.read_entry": "अपडेट पढ़ें",
    "changelog.next_entry": "आगे",
    "changelog.not_found": "अपडेट नहीं मिला",
    "changelog.back_to_changelog": "चेंजलॉग पर वापस जाएं",
    "changelog.eyebrow": "चेंजलॉग",
    "changelog.title": "Min ने क्या शिप किया",
    "changelog.subtitle": "WhatsApp को आसान, तेज़ और ज़्यादा उपयोगी बनाने वाले उत्पाद काम के छोटे अपडेट।"
  },
  "xh": {
    "common.language": "Ulwimi",
    "common.continue": "Qhubeka",
    "common.back": "Emva",
    "common.skip_for_now": "Tsiba okwangoku",
    "landing.sign_in_up": "Ngena / bhalisa",
    "landing.hero_line_1": "Yithi ndlela-ntle",
    "landing.hero_line_2": "kumanqaku elizwi amade.",
    "landing.subtitle": "UMin uyabhala aze ashwankathele amanqaku akho elizwi.\nInqaku lelizwi lemizuzu esi-7 lijika libe sisifundo semizuzwana esi-7.",
    "landing.cta": "Fumana inyanga e-1 simahla",
    "landing.free_forever": "Inyanga e-1 ye-Fun simahla",
    "landing.locale_prompt": "Ufuna ukubona eli phepha nge-{language}?",
    "landing.use_language": "Sebenzisa i-{language}",
    "landing.keep_english": "Gcina isiNgesi",
    "auth.language_eyebrow": "Khetha ulwimi lwakho",
    "auth.language_title": "Loluphi ulwimi ekufuneka uMin alusebenzise nawe?",
    "auth.language_copy": "Khetha ulwimi lwakho lwasekhaya kuqala, emva koko ezinye iilwimi anokuzisebenzisa uMin xa abantu bethumela i-voice note.",
    "auth.home_language": "Ulwimi lwasekhaya",
    "auth.other_languages": "Ezinye iilwimi anokuzisebenzisa uMin nawe",
    "auth.other_languages_help": "Khetha iilwimi oziqonda ngokwaneleyo ukuze ufumane izishwankathelo ngazo.",
    "auth.continue_with_language": "Qhubeka",
    "auth.linked_eyebrow": "Ixhunyiwe ngempumelelo",
    "auth.linked_title": "Yongeza i-imeyile yakho",
    "changelog.preview_eyebrow": "Uhlaziyo lwamva",
    "changelog.preview_cta": "Bona okutsha",
    "changelog.home": "Ekhaya",
    "changelog.details": "Iinkcukacha",
    "changelog.also_improved": "Kuphuculwe kwakhona",
    "changelog.image_alt": "Umzobo wohlelo lwechangelog kaMin malunga nokuthi {title}",
    "changelog.pagination": "Ukuhamba kwi-changelog",
    "changelog.read_entry": "Funda uhlaziyo",
    "changelog.next_entry": "Okulandelayo",
    "changelog.not_found": "Uhlaziyo alufumanekanga",
    "changelog.back_to_changelog": "Buyela kwi-changelog",
    "changelog.eyebrow": "Ingxelo yotshintsho",
    "changelog.title": "Okuthunyelwe nguMin",
    "changelog.subtitle": "Uhlaziyo olufutshane ngomsebenzi wemveliso owenza i-WhatsApp ibe lula, ikhawuleze, kwaye iluncedo ngakumbi."
  },
  "zu": {
    "common.language": "Ulimi",
    "common.continue": "Qhubeka",
    "common.back": "Emuva",
    "common.skip_for_now": "Yeqa okwamanje",
    "landing.sign_in_up": "Ngena / bhalisa",
    "landing.hero_line_1": "Valelisa",
    "landing.hero_line_2": "kumanothi ezwi amade.",
    "landing.subtitle": "UMin ubhala futhi afingqe amanothi akho ezwi.\nInothi lezwi lemizuzu engu-7 liphenduka ukufundwa kwemizuzwana engu-7.",
    "landing.cta": "Thola inyanga engu-1 mahhala",
    "landing.free_forever": "Inyanga engu-1 ye-Fun mahhala",
    "landing.locale_prompt": "Ufuna ukubona leli khasi nge-{language}?",
    "landing.use_language": "Sebenzisa i-{language}",
    "landing.keep_english": "Gcina isiNgisi",
    "auth.language_eyebrow": "Khetha ulimi lwakho",
    "auth.language_title": "IMin kufanele isebenzise luphi ulimi nawe?",
    "auth.language_copy": "Khetha ulimi lwakho lwasekhaya kuqala, bese ezinye izilimi iMin engazisebenzisa uma abantu bekuthumela ama-voice note.",
    "auth.home_language": "Ulimi lwasekhaya",
    "auth.other_languages": "Ezinye izilimi iMin engazisebenzisa nawe",
    "auth.other_languages_help": "Khetha izilimi oziqonda kahle ukuze uthole izifinyezo ngazo.",
    "auth.continue_with_language": "Qhubeka",
    "auth.linked_eyebrow": "Kuxhunyiwe ngempumelelo",
    "auth.linked_title": "Engeza i-imeyili yakho",
    "changelog.preview_eyebrow": "Isibuyekezo sakamuva",
    "changelog.preview_cta": "Bona okusha",
    "changelog.home": "Ekhaya",
    "changelog.details": "Imininingwane",
    "changelog.also_improved": "Kuphinde kwathuthukiswa",
    "changelog.image_alt": "Umfanekiso wohlelo lwe-changelog yeMin othi {title}",
    "changelog.pagination": "Ukuzulazula kwe-changelog",
    "changelog.read_entry": "Funda isibuyekezo",
    "changelog.next_entry": "Okulandelayo",
    "changelog.not_found": "Isibuyekezo asitholakalanga",
    "changelog.back_to_changelog": "Buyela ku-changelog",
    "changelog.eyebrow": "Irekhodi lezinguquko",
    "changelog.title": "Okukhishwe uMin",
    "changelog.subtitle": "Izibuyekezo ezimfishane ngomsebenzi womkhiqizo owenza i-WhatsApp ibe lula, isheshe, futhi ibe wusizo kakhulu."
  },
  "bn": {
    "common.language": "ভাষা",
    "common.continue": "চালিয়ে যান",
    "common.back": "ফিরে",
    "common.skip_for_now": "আপাতত এড়িয়ে যান",
    "landing.sign_in_up": "সাইন ইন/আপ করুন",
    "landing.hero_line_1": "দীর্ঘ ভয়েস নোটকে",
    "landing.hero_line_2": "বিদায় বলুন।",
    "landing.subtitle": "Min আপনার ভয়েস নোট লিখে দেয় এবং সংক্ষেপ করে।\n৭ মিনিটের ভয়েস নোট ৭ সেকেন্ডের পড়ায় পরিণত হয়।",
    "landing.cta": "১ মাস বিনামূল্যে পান",
    "landing.free_forever": "1 মাস Fun বিনামূল্যে",
    "landing.locale_prompt": "{language} এই পৃষ্ঠাটি দেখুন?",
    "landing.use_language": "{language} ব্যবহার করুন",
    "landing.keep_english": "ইংরেজি রাখুন",
    "auth.language_eyebrow": "আপনার ভাষা সেট করুন",
    "auth.language_title": "Min আপনার সাথে কোন ভাষা ব্যবহার করা উচিত?",
    "auth.language_copy": "প্রথমে আপনার হোম ল্যাঙ্গুয়েজ বেছে নিন, তারপর অন্য যেকোন ভাষা Min ব্যবহার করতে পারবেন যখন লোকেরা আপনাকে ভয়েস নোট পাঠাবে।",
    "auth.home_language": "বাড়ির ভাষা",
    "auth.other_languages": "অন্যান্য ভাষা Min আপনার সাথে ব্যবহার করতে পারে",
    "auth.other_languages_help": "সারাংশ পাওয়ার জন্য আপনি যথেষ্ট ভালো বোঝেন এমন ভাষা বেছে নিন।",
    "auth.continue_with_language": "চালিয়ে যান",
    "auth.linked_eyebrow": "সফলভাবে লিঙ্ক করা হয়েছে",
    "auth.linked_title": "আপনার ইমেল যোগ করুন",
    "changelog.preview_eyebrow": "সর্বশেষ আপডেট",
    "changelog.preview_cta": "নতুন কী দেখুন",
    "changelog.home": "হোম",
    "changelog.details": "বিস্তারিত",
    "changelog.also_improved": "আরও উন্নত হয়েছে",
    "changelog.image_alt": "{title}-এর জন্য Min changelog-এর সম্পাদকীয় শিল্পকর্ম",
    "changelog.pagination": "চেঞ্জলগ নেভিগেশন",
    "changelog.read_entry": "আপডেট পড়ুন",
    "changelog.next_entry": "পরেরটি",
    "changelog.not_found": "আপডেট পাওয়া যায়নি",
    "changelog.back_to_changelog": "চেঞ্জলগে ফিরুন",
    "changelog.eyebrow": "চেঞ্জলগ",
    "changelog.title": "Min যা পাঠিয়েছে",
    "changelog.subtitle": "WhatsApp-কে সহজ, দ্রুত এবং আরও দরকারি করতে পণ্যের ছোট আপডেট।"
  },
  "gu": {
    "common.language": "ભાષા",
    "common.continue": "ચાલુ રાખો",
    "common.back": "પાછળ",
    "common.skip_for_now": "હમણાં માટે અવગણો",
    "landing.sign_in_up": "સાઇન ઇન / અપ",
    "landing.hero_line_1": "લાંબી વૉઇસ નોટ્સને",
    "landing.hero_line_2": "અલવિદા કહો.",
    "landing.subtitle": "Min તમારી વૉઇસ નોટ્સને લખાણમાં ફેરવે છે અને સારાંશ આપે છે.\n7 મિનિટની વૉઇસ નોટ 7 સેકન્ડના વાંચનમાં ફેરવાય છે.",
    "landing.cta": "1 મહિનો મફત મેળવો",
    "landing.free_forever": "1 મહિનો Fun મફત",
    "landing.locale_prompt": "{language} માં આ પૃષ્ઠ જુઓ?",
    "landing.use_language": "{language} નો ઉપયોગ કરો",
    "landing.keep_english": "અંગ્રેજી રાખો",
    "auth.language_eyebrow": "તમારી ભાષા સેટ કરો",
    "auth.language_title": "Min તમારી સાથે કઈ ભાષાનો ઉપયોગ કરવો જોઈએ?",
    "auth.language_copy": "પહેલા તમારી હોમ લેંગ્વેજ પસંદ કરો, પછી લોકો તમને વૉઇસ નોટ્સ મોકલે ત્યારે અન્ય કોઈપણ ભાષા Min નો ઉપયોગ કરી શકે છે.",
    "auth.home_language": "ઘરની ભાષા",
    "auth.other_languages": "અન્ય ભાષાઓ Min તમારી સાથે ઉપયોગ કરી શકે છે",
    "auth.other_languages_help": "તમે સારાંશ પ્રાપ્ત કરવા માટે પૂરતી સારી રીતે સમજો છો તે ભાષાઓ પસંદ કરો.",
    "auth.continue_with_language": "ચાલુ રાખો",
    "auth.linked_eyebrow": "સફળતાપૂર્વક લિંક કર્યું",
    "auth.linked_title": "તમારું ઇમેઇલ ઉમેરો",
    "changelog.preview_eyebrow": "નવીનતમ અપડેટ",
    "changelog.preview_cta": "નવું શું છે જુઓ",
    "changelog.home": "હોમ",
    "changelog.details": "વિગતો",
    "changelog.also_improved": "આ પણ સુધારાયું",
    "changelog.image_alt": "{title} માટે Min changelogનું સંપાદકીય ચિત્ર",
    "changelog.pagination": "ચેન્જલોગ નેવિગેશન",
    "changelog.read_entry": "અપડેટ વાંચો",
    "changelog.next_entry": "આગળ",
    "changelog.not_found": "અપડેટ મળ્યું નથી",
    "changelog.back_to_changelog": "ચેન્જલોગ પર પાછા જાઓ",
    "changelog.eyebrow": "ચેન્જલોગ",
    "changelog.title": "Min એ શું મોકલ્યું",
    "changelog.subtitle": "WhatsApp ને વધુ સરળ, ઝડપી અને ઉપયોગી બનાવતા ઉત્પાદન કામ વિશે ટૂંકા અપડેટ્સ."
  },
  "kn": {
    "common.language": "ಭಾಷೆ",
    "common.continue": "ಮುಂದುವರಿಸಿ",
    "common.back": "ಹಿಂದೆ",
    "common.skip_for_now": "ಸದ್ಯಕ್ಕೆ ಬಿಟ್ಟುಬಿಡಿ",
    "landing.sign_in_up": "ಸೈನ್ ಇನ್ / ಅಪ್",
    "landing.hero_line_1": "ದೀರ್ಘ ವಾಯ್ಸ್ ನೋಟ್‌ಗಳಿಗೆ",
    "landing.hero_line_2": "ವಿದಾಯ ಹೇಳಿ.",
    "landing.subtitle": "Min ನಿಮ್ಮ ವಾಯ್ಸ್ ನೋಟ್‌ಗಳನ್ನು ಬರಹಕ್ಕೆ ತಂದು ಸಂಕ್ಷಿಪ್ತಗೊಳಿಸುತ್ತದೆ.\n7 ನಿಮಿಷದ ವಾಯ್ಸ್ ನೋಟ್ 7 ಸೆಕೆಂಡಿನ ಓದಾಗುತ್ತದೆ.",
    "landing.cta": "1 ತಿಂಗಳು ಉಚಿತವಾಗಿ ಪಡೆಯಿರಿ",
    "landing.free_forever": "1 ತಿಂಗಳು Fun ಉಚಿತ",
    "landing.locale_prompt": "{language} ನಲ್ಲಿ ಈ ಪುಟವನ್ನು ನೋಡುವುದೇ?",
    "landing.use_language": "{language} ಬಳಸಿ",
    "landing.keep_english": "ಇಂಗ್ಲಿಷ್ ಇಟ್ಟುಕೊಳ್ಳಿ",
    "auth.language_eyebrow": "ನಿಮ್ಮ ಭಾಷೆಯನ್ನು ಹೊಂದಿಸಿ",
    "auth.language_title": "Min ನಿಮ್ಮೊಂದಿಗೆ ಯಾವ ಭಾಷೆಯನ್ನು ಬಳಸಬೇಕು?",
    "auth.language_copy": "ಮೊದಲು ನಿಮ್ಮ ಮನೆ ಭಾಷೆಯನ್ನು ಆರಿಸಿ, ನಂತರ ಜನರು ನಿಮಗೆ ಧ್ವನಿ ಟಿಪ್ಪಣಿಗಳನ್ನು ಕಳುಹಿಸಿದಾಗ Min ಯಾವುದೇ ಇತರ ಭಾಷೆಗಳನ್ನು ಬಳಸಬಹುದು.",
    "auth.home_language": "ಮನೆ ಭಾಷೆ",
    "auth.other_languages": "ಇತರ ಭಾಷೆಗಳು Min ನಿಮ್ಮೊಂದಿಗೆ ಬಳಸಬಹುದು",
    "auth.other_languages_help": "ಸಾರಾಂಶಗಳನ್ನು ಸ್ವೀಕರಿಸಲು ನೀವು ಚೆನ್ನಾಗಿ ಅರ್ಥಮಾಡಿಕೊಳ್ಳುವ ಭಾಷೆಗಳನ್ನು ಆಯ್ಕೆಮಾಡಿ.",
    "auth.continue_with_language": "ಮುಂದುವರಿಸಿ",
    "auth.linked_eyebrow": "ಯಶಸ್ವಿಯಾಗಿ ಲಿಂಕ್ ಮಾಡಲಾಗಿದೆ",
    "auth.linked_title": "ನಿಮ್ಮ ಇಮೇಲ್ ಸೇರಿಸಿ",
    "changelog.preview_eyebrow": "ಇತ್ತೀಚಿನ ನವೀಕರಣ",
    "changelog.preview_cta": "ಹೊಸದೇನು ನೋಡಿ",
    "changelog.home": "ಮುಖಪುಟ",
    "changelog.details": "ವಿವರಗಳು",
    "changelog.also_improved": "ಇದೂ ಸುಧಾರಿಸಲಾಗಿದೆ",
    "changelog.image_alt": "{title}ಗಾಗಿ Min changelog ನ ಸಂಪಾದಕೀಯ ಚಿತ್ರ",
    "changelog.pagination": "ಚೇಂಜ್‌ಲಾಗ್ ನ್ಯಾವಿಗೇಶನ್",
    "changelog.read_entry": "ನವೀಕರಣ ಓದಿ",
    "changelog.next_entry": "ಮುಂದೆ",
    "changelog.not_found": "ನವೀಕರಣ ಕಂಡುಬಂದಿಲ್ಲ",
    "changelog.back_to_changelog": "ಚೇಂಜ್\u200cಲಾಗ್\u200cಗೆ ಹಿಂತಿರುಗಿ",
    "changelog.eyebrow": "ಚೇಂಜ್\u200cಲಾಗ್",
    "changelog.title": "Min ಏನು ಶಿಪ್ ಮಾಡಿದೆ",
    "changelog.subtitle": "WhatsApp ಅನ್ನು ಸುಲಭ, ವೇಗವಾದ ಮತ್ತು ಹೆಚ್ಚು ಉಪಯುಕ್ತವಾಗಿಸುವ ಉತ್ಪನ್ನ ಕೆಲಸದ ಚಿಕ್ಕ ನವೀಕರಣಗಳು."
  },
  "ml": {
    "common.language": "ഭാഷ",
    "common.continue": "തുടരുക",
    "common.back": "തിരികെ",
    "common.skip_for_now": "തൽക്കാലം ഒഴിവാക്കുക",
    "landing.sign_in_up": "സൈൻ ഇൻ / അപ്പ് ചെയ്യുക",
    "landing.hero_line_1": "നീണ്ട വോയ്സ് നോട്ടുകളോട്",
    "landing.hero_line_2": "വിട പറയൂ.",
    "landing.subtitle": "Min നിങ്ങളുടെ വോയ്സ് നോട്ടുകൾ എഴുത്താക്കി ചുരുക്കുന്നു.\n7 മിനിറ്റ് വോയ്സ് നോട്ട് 7 സെക്കൻഡ് വായനയായി മാറുന്നു.",
    "landing.cta": "1 മാസം സൗജന്യമായി നേടൂ",
    "landing.free_forever": "ഒരു മാസം Fun സൗജന്യം",
    "landing.locale_prompt": "{language}-ൽ ഈ പേജ് കാണണോ?",
    "landing.use_language": "{language} ഉപയോഗിക്കുക",
    "landing.keep_english": "ഇംഗ്ലീഷ് സൂക്ഷിക്കുക",
    "auth.language_eyebrow": "നിങ്ങളുടെ ഭാഷ സജ്ജമാക്കുക",
    "auth.language_title": "Min നിങ്ങളോടൊപ്പം ഏത് ഭാഷയാണ് ഉപയോഗിക്കേണ്ടത്?",
    "auth.language_copy": "ആദ്യം നിങ്ങളുടെ ഹോം ഭാഷ തിരഞ്ഞെടുക്കുക, തുടർന്ന് ആളുകൾ നിങ്ങൾക്ക് വോയ്‌സ് നോട്ടുകൾ അയയ്‌ക്കുമ്പോൾ Min മറ്റ് ഏത് ഭാഷകളും ഉപയോഗിക്കാനാകും.",
    "auth.home_language": "വീട്ടിലെ ഭാഷ",
    "auth.other_languages": "മറ്റ് ഭാഷകൾ Min നിങ്ങൾക്ക് ഉപയോഗിക്കാനാകും",
    "auth.other_languages_help": "സംഗ്രഹങ്ങൾ ലഭിക്കുന്നതിന് നിങ്ങൾക്ക് നന്നായി മനസ്സിലാകുന്ന ഭാഷകൾ തിരഞ്ഞെടുക്കുക.",
    "auth.continue_with_language": "തുടരുക",
    "auth.linked_eyebrow": "ലിങ്ക് ചെയ്‌തു",
    "auth.linked_title": "നിങ്ങളുടെ ഇമെയിൽ ചേർക്കുക",
    "changelog.preview_eyebrow": "ഏറ്റവും പുതിയ അപ്ഡേറ്റ്",
    "changelog.preview_cta": "പുതിയത് കാണുക",
    "changelog.home": "ഹോം",
    "changelog.details": "വിശദാംശങ്ങൾ",
    "changelog.also_improved": "ഇതും മെച്ചപ്പെടുത്തി",
    "changelog.image_alt": "{title} എന്നതിനുള്ള Min changelog എഡിറ്റോറിയൽ ചിത്രണം",
    "changelog.pagination": "ചേഞ്ച്‌ലോഗ് നാവിഗേഷൻ",
    "changelog.read_entry": "അപ്ഡേറ്റ് വായിക്കുക",
    "changelog.next_entry": "അടുത്തത്",
    "changelog.not_found": "അപ്ഡേറ്റ് കണ്ടെത്താനായില്ല",
    "changelog.back_to_changelog": "ചേഞ്ച്\u200cലോഗിലേക്ക് മടങ്ങുക",
    "changelog.eyebrow": "ചേഞ്ച്\u200cലോഗ്",
    "changelog.title": "Min എന്ത് ഷിപ്പ് ചെയ്തു",
    "changelog.subtitle": "WhatsApp എളുപ്പവും വേഗവും കൂടുതൽ ഉപകാരപ്രദവുമാക്കുന്ന ഉൽപ്പന്ന ജോലിയുടെ ചെറിയ അപ്ഡേറ്റുകൾ."
  },
  "mr": {
    "common.language": "भाषा",
    "common.continue": "चालू ठेवा",
    "common.back": "मागे",
    "common.skip_for_now": "आतासाठी वगळा",
    "landing.sign_in_up": "साइन इन/अप करा",
    "landing.hero_line_1": "लांब व्हॉइस नोट्सना",
    "landing.hero_line_2": "निरोप द्या.",
    "landing.subtitle": "Min तुमच्या व्हॉइस नोट्सचे लिखित रूपांतर आणि सारांश करते.\n7 मिनिटांची व्हॉइस नोट 7 सेकंदांच्या वाचनात बदलते.",
    "landing.cta": "1 महिना मोफत मिळवा",
    "landing.free_forever": "Fun विनामूल्य 1 महिना",
    "landing.locale_prompt": "{language} मध्ये हे पृष्ठ पहा?",
    "landing.use_language": "{language} वापरा",
    "landing.keep_english": "इंग्रजी ठेवा",
    "auth.language_eyebrow": "तुमची भाषा सेट करा",
    "auth.language_title": "Min तुमच्यासोबत कोणती भाषा वापरावी?",
    "auth.language_copy": "प्रथम तुमची मातृभाषा निवडा, नंतर लोक तुम्हाला व्हॉइस नोट्स पाठवतात तेव्हा Min इतर कोणत्याही भाषा वापरू शकतात.",
    "auth.home_language": "घरची भाषा",
    "auth.other_languages": "इतर भाषा Min तुमच्यासोबत वापरू शकतात",
    "auth.other_languages_help": "सारांश प्राप्त करण्यासाठी तुम्हाला पुरेशी समजणारी भाषा निवडा.",
    "auth.continue_with_language": "चालू ठेवा",
    "auth.linked_eyebrow": "यशस्वीरित्या लिंक केले",
    "auth.linked_title": "तुमचा ईमेल जोडा",
    "changelog.preview_eyebrow": "नवीनतम अपडेट",
    "changelog.preview_cta": "नवीन काय आहे ते पहा",
    "changelog.home": "होम",
    "changelog.details": "तपशील",
    "changelog.also_improved": "हेही सुधारले",
    "changelog.image_alt": "{title} साठी Min changelog चे संपादकीय चित्र",
    "changelog.pagination": "चेंजलॉग नेव्हिगेशन",
    "changelog.read_entry": "अपडेट वाचा",
    "changelog.next_entry": "पुढे",
    "changelog.not_found": "अपडेट सापडले नाही",
    "changelog.back_to_changelog": "चेंजलॉगकडे परत जा",
    "changelog.eyebrow": "चेंजलॉग",
    "changelog.title": "Min ने काय शिप केले",
    "changelog.subtitle": "WhatsApp सोपे, जलद आणि अधिक उपयोगी बनवणाऱ्या उत्पादन कामाचे छोटे अपडेट."
  },
  "or": {
    "common.language": "ଭାଷା",
    "common.continue": "ଜାରି ରଖ",
    "common.back": "ପଛକୁ",
    "common.skip_for_now": "ବର୍ତ୍ତମାନ ପାଇଁ ଛାଡିଦିଅ",
    "landing.sign_in_up": "ସାଇନ୍ ଅପ୍ କରନ୍ତୁ",
    "landing.hero_line_1": "ଲମ୍ବା ଭଏସ୍ ନୋଟ୍‌କୁ",
    "landing.hero_line_2": "ବିଦାୟ ଦିଅନ୍ତୁ।",
    "landing.subtitle": "Min ଆପଣଙ୍କ ଭଏସ୍ ନୋଟ୍‌କୁ ଲେଖାରେ ପରିଣତ କରି ସାରାଂଶ ଦିଏ।\n7 ମିନିଟ୍‌ର ଭଏସ୍ ନୋଟ୍ 7 ସେକେଣ୍ଡ୍‌ର ପଢ଼ାରେ ପରିଣତ ହୁଏ।",
    "landing.cta": "୧ ମାସ ମାଗଣା ପାଆନ୍ତୁ",
    "landing.free_forever": "Fun ର 1 ମାସ ମାଗଣା",
    "landing.locale_prompt": "{language} ରେ ଏହି ପୃଷ୍ଠା ଦେଖନ୍ତୁ?",
    "landing.use_language": "{language} ବ୍ୟବହାର କରନ୍ତୁ",
    "landing.keep_english": "ଇଂରାଜୀ ରଖନ୍ତୁ",
    "auth.language_eyebrow": "ତୁମର ଭାଷା ସେଟ୍ କର",
    "auth.language_title": "Min ଆପଣଙ୍କ ସହିତ କେଉଁ ଭାଷା ବ୍ୟବହାର କରିବା ଉଚିତ୍?",
    "auth.language_copy": "ପ୍ରଥମେ ତୁମର ଘରୋଇ ଭାଷା ବାଛ, ତାପରେ ଅନ୍ୟ କ languages ଣସି ଭାଷା Min ବ୍ୟବହାର କରିପାରିବ ଯେତେବେଳେ ଲୋକମାନେ ତୁମକୁ ଭଏସ୍ ନୋଟ୍ ପଠାନ୍ତି",
    "auth.home_language": "ମୂଳ ଭାଷା",
    "auth.other_languages": "ଅନ୍ୟ ଭାଷା Min ଆପଣଙ୍କ ସହିତ ବ୍ୟବହାର କରିପାରିବ",
    "auth.other_languages_help": "ସାରାଂଶ ଗ୍ରହଣ କରିବାକୁ ତୁମେ ଭଲଭାବେ ବୁ understand ୁଥିବା ଭାଷା ବାଛ",
    "auth.continue_with_language": "ଜାରି ରଖ",
    "auth.linked_eyebrow": "ସଫଳତାର ସହିତ ଲିଙ୍କ୍ ହୋଇଛି",
    "auth.linked_title": "ଆପଣଙ୍କର ଇମେଲ୍ ଯୋଡନ୍ତୁ",
    "changelog.preview_eyebrow": "ନୂତନ ଅପଡେଟ୍",
    "changelog.preview_cta": "ନୂଆ କଣ ଦେଖନ୍ତୁ",
    "changelog.home": "ହୋମ୍",
    "changelog.details": "ବିବରଣୀ",
    "changelog.also_improved": "ଏହା ମଧ୍ୟ ଉନ୍ନତ ହେଲା",
    "changelog.image_alt": "{title} ପାଇଁ Min changelog ର ସମ୍ପାଦକୀୟ ଚିତ୍ର",
    "changelog.pagination": "ଚେଞ୍ଜଲଗ୍ ନେଭିଗେସନ୍",
    "changelog.read_entry": "ଅପଡେଟ୍ ପଢନ୍ତୁ",
    "changelog.next_entry": "ପରବର୍ତ୍ତୀ",
    "changelog.not_found": "ଅପଡେଟ୍ ମିଳିଲା ନାହିଁ",
    "changelog.back_to_changelog": "ଚେଞ୍ଜଲଗ୍\u200cକୁ ଫେରନ୍ତୁ",
    "changelog.eyebrow": "ଚେଞ୍ଜଲଗ୍",
    "changelog.title": "Min କଣ ଶିପ୍ କଲା",
    "changelog.subtitle": "WhatsApp କୁ ସହଜ, ଦ୍ରୁତ ଏବଂ ଅଧିକ ଉପଯୋଗୀ କରୁଥିବା ପ୍ରୋଡକ୍ଟ କାମର ଛୋଟ ଅପଡେଟ୍।"
  },
  "pa": {
    "common.language": "ਭਾਸ਼ਾ",
    "common.continue": "ਜਾਰੀ ਰੱਖੋ",
    "common.back": "ਪਿੱਛੇ",
    "common.skip_for_now": "ਹੁਣ ਲਈ ਛੱਡੋ",
    "landing.sign_in_up": "ਸਾਈਨ ਇਨ/ਅੱਪ ਕਰੋ",
    "landing.hero_line_1": "ਲੰਬੇ ਵੌਇਸ ਨੋਟਾਂ ਨੂੰ",
    "landing.hero_line_2": "ਅਲਵਿਦਾ ਕਹੋ।",
    "landing.subtitle": "Min ਤੁਹਾਡੇ ਵੌਇਸ ਨੋਟਾਂ ਨੂੰ ਲਿਖਤ ਵਿੱਚ ਬਦਲਦਾ ਅਤੇ ਸੰਖੇਪ ਕਰਦਾ ਹੈ।\n7 ਮਿੰਟ ਦਾ ਵੌਇਸ ਨੋਟ 7 ਸਕਿੰਟ ਦੇ ਪੜ੍ਹਨ ਵਿੱਚ ਬਦਲ ਜਾਂਦਾ ਹੈ।",
    "landing.cta": "1 ਮਹੀਨਾ ਮੁਫ਼ਤ ਪਾਓ",
    "landing.free_forever": "Fun ਦਾ 1 ਮਹੀਨਾ ਮੁਫ਼ਤ",
    "landing.locale_prompt": "ਇਸ ਪੰਨੇ ਨੂੰ {language} ਵਿੱਚ ਦੇਖੋ?",
    "landing.use_language": "{language} ਵਰਤੋ",
    "landing.keep_english": "ਅੰਗਰੇਜ਼ੀ ਰੱਖੋ",
    "auth.language_eyebrow": "ਆਪਣੀ ਭਾਸ਼ਾ ਸੈੱਟ ਕਰੋ",
    "auth.language_title": "Min ਤੁਹਾਡੇ ਨਾਲ ਕਿਹੜੀ ਭਾਸ਼ਾ ਦੀ ਵਰਤੋਂ ਕਰਨੀ ਚਾਹੀਦੀ ਹੈ?",
    "auth.language_copy": "ਪਹਿਲਾਂ ਆਪਣੀ ਘਰੇਲੂ ਭਾਸ਼ਾ ਚੁਣੋ, ਫਿਰ ਕੋਈ ਹੋਰ ਭਾਸ਼ਾ Min ਵਰਤ ਸਕਦੀ ਹੈ ਜਦੋਂ ਲੋਕ ਤੁਹਾਨੂੰ ਵੌਇਸ ਨੋਟ ਭੇਜਦੇ ਹਨ।",
    "auth.home_language": "ਘਰੇਲੂ ਭਾਸ਼ਾ",
    "auth.other_languages": "ਹੋਰ ਭਾਸ਼ਾਵਾਂ Min ਤੁਹਾਡੇ ਨਾਲ ਵਰਤ ਸਕਦੇ ਹਨ",
    "auth.other_languages_help": "ਉਹਨਾਂ ਭਾਸ਼ਾਵਾਂ ਦੀ ਚੋਣ ਕਰੋ ਜਿਹਨਾਂ ਵਿੱਚ ਤੁਸੀਂ ਸੰਖੇਪ ਜਾਣਕਾਰੀ ਪ੍ਰਾਪਤ ਕਰਨ ਲਈ ਚੰਗੀ ਤਰ੍ਹਾਂ ਸਮਝਦੇ ਹੋ।",
    "auth.continue_with_language": "ਜਾਰੀ ਰੱਖੋ",
    "auth.linked_eyebrow": "ਸਫਲਤਾਪੂਰਵਕ ਲਿੰਕ ਕੀਤਾ ਗਿਆ",
    "auth.linked_title": "ਆਪਣੀ ਈਮੇਲ ਸ਼ਾਮਲ ਕਰੋ",
    "changelog.preview_eyebrow": "ਨਵਾਂ ਅਪਡੇਟ",
    "changelog.preview_cta": "ਨਵਾਂ ਕੀ ਹੈ ਵੇਖੋ",
    "changelog.home": "ਹੋਮ",
    "changelog.details": "ਵੇਰਵੇ",
    "changelog.also_improved": "ਇਹ ਵੀ ਸੁਧਾਰਿਆ",
    "changelog.image_alt": "{title} ਲਈ Min changelog ਦੀ ਸੰਪਾਦਕੀ ਤਸਵੀਰ",
    "changelog.pagination": "ਚੇਂਜਲਾਗ ਨੈਵੀਗੇਸ਼ਨ",
    "changelog.read_entry": "ਅਪਡੇਟ ਪੜ੍ਹੋ",
    "changelog.next_entry": "ਅਗਲਾ",
    "changelog.not_found": "ਅਪਡੇਟ ਨਹੀਂ ਮਿਲਿਆ",
    "changelog.back_to_changelog": "ਚੇਂਜਲਾਗ ਤੇ ਵਾਪਸ ਜਾਓ",
    "changelog.eyebrow": "ਚੇਂਜਲਾਗ",
    "changelog.title": "Min ਨੇ ਕੀ ਸ਼ਿਪ ਕੀਤਾ",
    "changelog.subtitle": "WhatsApp ਨੂੰ ਆਸਾਨ, ਤੇਜ਼ ਅਤੇ ਹੋਰ ਲਾਭਦਾਇਕ ਬਣਾਉਣ ਵਾਲੇ ਉਤਪਾਦ ਕੰਮ ਦੇ ਛੋਟੇ ਅਪਡੇਟ।"
  },
  "ta": {
    "common.language": "மொழி",
    "common.continue": "தொடரவும்",
    "common.back": "மீண்டும்",
    "common.skip_for_now": "இப்போதைக்கு தவிர்க்கவும்",
    "landing.sign_in_up": "உள்நுழையவும் / அப் செய்யவும்",
    "landing.hero_line_1": "நீண்ட குரல் குறிப்புகளுக்கு",
    "landing.hero_line_2": "விடை கொடுங்கள்.",
    "landing.subtitle": "Min உங்கள் குரல் குறிப்புகளை எழுத்தாக்கி சுருக்குகிறது.\n7 நிமிட குரல் குறிப்பு 7 விநாடி வாசிப்பாக மாறுகிறது.",
    "landing.cta": "1 மாதம் இலவசமாகப் பெறுங்கள்",
    "landing.free_forever": "1 மாதம் Fun இலவசம்",
    "landing.locale_prompt": "{language} இல் இந்தப் பக்கத்தைப் பார்க்கவா?",
    "landing.use_language": "{language} ஐப் பயன்படுத்தவும்",
    "landing.keep_english": "ஆங்கிலத்தை வைத்திருங்கள்",
    "auth.language_eyebrow": "உங்கள் மொழியை அமைக்கவும்",
    "auth.language_title": "Min உங்களுடன் எந்த மொழியைப் பயன்படுத்த வேண்டும்?",
    "auth.language_copy": "முதலில் உங்கள் வீட்டு மொழியைத் தேர்ந்தெடுங்கள், பிறகு பிற மொழிகள் Min மக்கள் உங்களுக்கு குரல் குறிப்புகளை அனுப்பும்போது பயன்படுத்த முடியும்.",
    "auth.home_language": "வீட்டு மொழி",
    "auth.other_languages": "பிற மொழிகளை Min உங்களுடன் பயன்படுத்தலாம்",
    "auth.other_languages_help": "சுருக்கங்களைப் பெறுவதற்கு நீங்கள் நன்கு புரிந்துகொள்ளும் மொழிகளைத் தேர்ந்தெடுக்கவும்.",
    "auth.continue_with_language": "தொடரவும்",
    "auth.linked_eyebrow": "வெற்றிகரமாக இணைக்கப்பட்டது",
    "auth.linked_title": "உங்கள் மின்னஞ்சலைச் சேர்க்கவும்",
    "changelog.preview_eyebrow": "சமீபத்திய புதுப்பிப்பு",
    "changelog.preview_cta": "புதியதைப் பார்க்கவும்",
    "changelog.home": "முகப்பு",
    "changelog.details": "விவரங்கள்",
    "changelog.also_improved": "இதுவும் மேம்பட்டது",
    "changelog.image_alt": "{title}க்கான Min changelog தொகுப்பாசிரியர் கலைப்படைப்பு",
    "changelog.pagination": "மாற்றப் பதிவு வழிசெலுத்தல்",
    "changelog.read_entry": "புதுப்பிப்பைப் படிக்கவும்",
    "changelog.next_entry": "அடுத்து",
    "changelog.not_found": "புதுப்பிப்பு கிடைக்கவில்லை",
    "changelog.back_to_changelog": "மாற்றப் பதிவுக்கு திரும்பவும்",
    "changelog.eyebrow": "மாற்றப் பதிவு",
    "changelog.title": "Min வெளியிட்டது",
    "changelog.subtitle": "WhatsApp-ஐ எளிதாக, வேகமாக, மேலும் பயனுள்ளதாக மாற்றும் தயாரிப்பு பணியின் குறுகிய புதுப்பிப்புகள்."
  },
  "te": {
    "common.language": "భాష",
    "common.continue": "కొనసాగించు",
    "common.back": "వెనుకకు",
    "common.skip_for_now": "ప్రస్తుతానికి దాటవేయి",
    "landing.sign_in_up": "సైన్ ఇన్ / అప్ చేయండి",
    "landing.hero_line_1": "సుదీర్ఘ వాయిస్ నోట్లకు",
    "landing.hero_line_2": "వీడ్కోలు చెప్పండి.",
    "landing.subtitle": "Min మీ వాయిస్ నోట్లను వచనంగా మార్చి సంక్షిప్తం చేస్తుంది.\n7 నిమిషాల వాయిస్ నోట్ 7 సెకన్ల చదువుగా మారుతుంది.",
    "landing.cta": "1 నెల ఉచితంగా పొందండి",
    "landing.free_forever": "1 నెల Fun ఉచితం",
    "landing.locale_prompt": "{language}లో ఈ పేజీని చూడాలా?",
    "landing.use_language": "{language}ని ఉపయోగించండి",
    "landing.keep_english": "ఇంగ్లీష్ ఉంచండి",
    "auth.language_eyebrow": "మీ భాషను సెట్ చేయండి",
    "auth.language_title": "Min మీతో ఏ భాషను ఉపయోగించాలి?",
    "auth.language_copy": "ముందుగా మీ ఇంటి భాషను ఎంచుకోండి, ఆపై వ్యక్తులు మీకు వాయిస్ నోట్స్ పంపినప్పుడు Min ఏవైనా ఇతర భాషలను ఉపయోగించవచ్చు.",
    "auth.home_language": "ఇంటి భాష",
    "auth.other_languages": "ఇతర భాషలు Min మీతో ఉపయోగించవచ్చు",
    "auth.other_languages_help": "సారాంశాలను స్వీకరించడానికి మీకు బాగా అర్థమయ్యే భాషలను ఎంచుకోండి.",
    "auth.continue_with_language": "కొనసాగించు",
    "auth.linked_eyebrow": "విజయవంతంగా లింక్ చేయబడింది",
    "auth.linked_title": "మీ ఇమెయిల్‌ను జోడించండి",
    "changelog.preview_eyebrow": "తాజా అప్డేట్",
    "changelog.preview_cta": "కొత్తవి చూడండి",
    "changelog.home": "హోమ్",
    "changelog.details": "వివరాలు",
    "changelog.also_improved": "ఇదీ మెరుగుపడింది",
    "changelog.image_alt": "{title} కోసం Min changelog సంపాదకీయ చిత్రం",
    "changelog.pagination": "చేంజ్‌లాగ్ నావిగేషన్",
    "changelog.read_entry": "అప్డేట్ చదవండి",
    "changelog.next_entry": "తదుపరి",
    "changelog.not_found": "అప్డేట్ దొరకలేదు",
    "changelog.back_to_changelog": "చేంజ్\u200cలాగ్\u200cకి తిరిగి వెళ్లండి",
    "changelog.eyebrow": "చేంజ్\u200cలాగ్",
    "changelog.title": "Min షిప్ చేసినవి",
    "changelog.subtitle": "WhatsApp ను సులభంగా, వేగంగా, మరింత ఉపయోగకరంగా చేసే ఉత్పత్తి పనిపై చిన్న అప్డేట్లు."
  },
  "ur": {
    "common.language": "زبان",
    "common.continue": "جاری رکھیں",
    "common.back": "پیچھے",
    "common.skip_for_now": "ابھی کے لیے چھوڑ دیں۔",
    "landing.sign_in_up": "سائن ان/اپ کریں۔",
    "landing.hero_line_1": "طویل وائس نوٹس کو",
    "landing.hero_line_2": "الوداع کہیں۔",
    "landing.subtitle": "Min آپ کے وائس نوٹس کو تحریر اور مختصر کرتا ہے۔\n7 منٹ کا وائس نوٹ 7 سیکنڈ کی پڑھائی میں بدل جاتا ہے۔",
    "landing.cta": "1 ماہ مفت حاصل کریں",
    "landing.free_forever": "Fun کا 1 ماہ مفت",
    "landing.locale_prompt": "یہ صفحہ {language} میں دیکھیں؟",
    "landing.use_language": "{language} استعمال کریں۔",
    "landing.keep_english": "انگریزی رکھو",
    "auth.language_eyebrow": "اپنی زبان سیٹ کریں۔",
    "auth.language_title": "Min کو آپ کے ساتھ کون سی زبان استعمال کرنی چاہیے؟",
    "auth.language_copy": "پہلے اپنی گھریلو زبان منتخب کریں، پھر کوئی بھی دوسری زبان Min استعمال کر سکتی ہے جب لوگ آپ کو صوتی نوٹ بھیجیں۔",
    "auth.home_language": "گھریلو زبان",
    "auth.other_languages": "دیگر زبانیں Min آپ کے ساتھ استعمال کر سکتی ہیں۔",
    "auth.other_languages_help": "ان زبانوں کا انتخاب کریں جن میں آپ خلاصے حاصل کرنے کے لیے کافی اچھی طرح سمجھتے ہیں۔",
    "auth.continue_with_language": "جاری رکھیں",
    "auth.linked_eyebrow": "کامیابی سے لنک ہو گیا۔",
    "auth.linked_title": "اپنا ای میل شامل کریں۔",
    "changelog.preview_eyebrow": "تازہ ترین اپ ڈیٹ",
    "changelog.preview_cta": "دیکھیں کیا نیا ہے",
    "changelog.home": "ہوم",
    "changelog.details": "تفصیلات",
    "changelog.also_improved": "یہ بھی بہتر ہوا",
    "changelog.image_alt": "{title} کے لیے Min changelog کی ادارتی تصویر",
    "changelog.pagination": "چینج لاگ نیویگیشن",
    "changelog.read_entry": "اپ ڈیٹ پڑھیں",
    "changelog.next_entry": "اگلا",
    "changelog.not_found": "اپ ڈیٹ نہیں ملا",
    "changelog.back_to_changelog": "چینج لاگ پر واپس جائیں",
    "changelog.eyebrow": "چینج لاگ",
    "changelog.title": "Min نے کیا جاری کیا",
    "changelog.subtitle": "WhatsApp کو آسان، تیز اور زیادہ مفید بنانے والے پروڈکٹ کام کی مختصر اپ ڈیٹس۔"
  }
};

function readStoredLocale() {
  if (typeof window === "undefined") {
    return "";
  }
  return normalizeUiLanguageCode(window.localStorage.getItem(STORAGE_KEY) || "", "");
}

function writeStoredLocale(languageCode) {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, normalizeUiLanguageCode(languageCode));
}

const I18nContext = createContext({
  locale: DEFAULT_LANGUAGE_CODE,
  setLocale: () => {},
  t: (key, values) => {
    let template = String(key || "");
    for (const [name, value] of Object.entries(values || {})) {
      template = template.replaceAll(`{${name}}`, String(value ?? ""));
    }
    return template;
  },
  tx: (source) => String(source || ""),
});

export function I18nProvider({
  children,
  initialLocale = "",
  persistLocale = true,
}) {
  const [locale, setLocaleState] = useState(
    () => resolveInitialLocale(initialLocale, readStoredLocale()),
  );

  useEffect(() => {
    const normalized = normalizeUiLanguageCode(locale);
    if (typeof document !== "undefined") {
      document.documentElement.lang = normalized;
      document.documentElement.dir = getLanguageByCode(normalized).rtl ? "rtl" : "ltr";
    }
    if (persistLocale) {
      writeStoredLocale(normalized);
    }
  }, [locale, persistLocale]);

  const value = useMemo(() => ({
    locale: normalizeUiLanguageCode(locale),
    setLocale: (nextLocale) => setLocaleState(normalizeUiLanguageCode(nextLocale)),
    t: (key, values = {}) => {
      const normalizedLocale = normalizeUiLanguageCode(locale);
      const template = MESSAGES[normalizedLocale]?.[key]
        || MESSAGES[DEFAULT_LANGUAGE_CODE]?.[key]
        || String(key || "");
      return Object.entries(values).reduce(
        (current, [name, value]) => current.replaceAll(`{${name}}`, String(value ?? "")),
        template,
      );
    },
    tx: (source) => translateUiCopy(source, locale) || String(source || ""),
  }), [locale]);

  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n() {
  return useContext(I18nContext);
}
